import { Injectable, Inject } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class PlanningDragAndDropService {

  constructor(
    @Inject('confirmDialogService') private confirmDialogService: any,
    @Inject('planningAlertsService') private planningAlertsService: any,
    @Inject('planningDialogService') private planningDialogService: any,
    @Inject('$timeout') private $timeout: any
  ) {}

  // --------------------------------------------------------------------------------
  // 1. planning-draft-routes-panel-list.component.ts & planning-draft-routes-table-list.component.ts
  // --------------------------------------------------------------------------------
  public onDropRoutes(
    route: any,
    isAllowDraggingIntoDispatchedRoute: boolean,
    event: CdkDragDrop<any[]>,
    selectedJobIds: string[],
    jobs: any[],
    panels: any[],
    showDropAnimation: (x: number, y: number, count: number, isMultiple: boolean) => void
  ): Promise<any | null> {
    if (!event.isPointerOverContainer || event.container === event.previousContainer || _.get(event.item.data, 'activities')) {
      return Promise.resolve(null);
    }

    const isJob = !!_.get(event.item.data, 'jobType');
    const isActivity = !!_.get(event.item.data, 'stopType');
    const jobId = isJob ? _.get(event.item.data, 'id') : _.get(event.item.data, 'jobId');
    const eventIds = event.item.data.jobIds || [jobId];
    const jobIds = _.intersectionWith(eventIds, selectedJobIds, _.isEqual)?.length ?
      selectedJobIds :
      eventIds;

    if ((!jobId && !jobIds) || (!isJob && !jobIds && !isActivity)) {
      return Promise.resolve(null);
    }

    const isPanel = panels?.some(p => p.id === event.previousContainer.id);
    const fromRouteId = isPanel ? null : event.previousContainer.id;
    const fromOrderedIds = fromRouteId ? this._getStopsOrderIds(event.previousContainer.data) : null;

    // Get drop position and determine if multiple items
    const rect = event.container.element.nativeElement.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    // Check if multiple items are being dropped
    const dropCount = jobIds.length;

    // Show animation after drop with appropriate styling
    this.$timeout(() => {
      showDropAnimation(x, y, dropCount, dropCount > 1);
    }, 50);

    if (jobIds.length >= 10) {
      return this.confirmDialogService.confirmWithInfo('Confirm', 'MOVE_X_JOBS_CONFIRM', '', {x: jobIds.length}, event)
        .then(() => {
          return this._displayJobsWarningsRoutes(route, isAllowDraggingIntoDispatchedRoute, jobIds, event, fromRouteId, fromOrderedIds, jobs);
        })
        .catch(() => null);
    }

    return this._displayJobsWarningsRoutes(route, isAllowDraggingIntoDispatchedRoute, jobIds, event, fromRouteId, fromOrderedIds, jobs);
  }

  private _displayJobsWarningsRoutes(
    route: any,
    isAllowDraggingIntoDispatchedRoute: boolean,
    jobIds: string[],
    event: CdkDragDrop<any[]>,
    fromRouteId: string | null,
    fromOrderedIds: string[] | null,
    jobs: any[]
  ): Promise<any | null> {
    const errorCounts: Record<string, number> = {};
    const jobsToMove = [];
    const jobsToAdd = [];
    const stopIds = [];

    jobIds.forEach((id) => {
      const job = _.find(jobs, {id: id});
      if (job) {
        const error = this.planningAlertsService.getJobValidationError(job, route, { isAllowDraggingIntoDispatchedRoute });

        jobsToAdd.push(job);

        if (!error) {
          jobsToMove.push(job);
          stopIds.push(..._.map(_.get(job, 'stops') || [], 'id'));
        } else {
          errorCounts[error] = (errorCounts[error] || 0) + 1;
        }
      }
    });

    const warnings: { message: string, type?: string, data?: any }[] = this.planningDialogService.getJobsSkillMismatchWarnings(jobsToAdd, route.resource);
    warnings.push(...this.planningDialogService.getJobsCapacityWarnings(jobsToAdd, route));
    const hasSkillMismatch = warnings.some((w) => w.type === 'skillMismatch');

    if (warnings.length) {
      return this.planningDialogService.openJobAssignWarnings(warnings, event)
        .then(() => {
          return this._dropJobsRoutes(route, jobsToMove, errorCounts, stopIds, fromRouteId, fromOrderedIds, hasSkillMismatch);
        })
        .catch(() => null);
    } else {
      return Promise.resolve(this._dropJobsRoutes(route, jobsToMove, errorCounts, stopIds, fromRouteId, fromOrderedIds, hasSkillMismatch));
    }
  }

  private _dropJobsRoutes(
    route: any,
    jobs: any[],
    errorCounts: Record<string, number>,
    stopIds: string[],
    fromRouteId: string | null,
    fromOrderedIds: string[] | null,
    allowSkillsMismatch?: boolean
  ) {
    const errorKeys = Object.keys(errorCounts);

    if (errorKeys.length >= 1) {
      this.planningAlertsService.bulkDropJobsError(errorCounts);
    }

    if (!jobs.length) {
      return null;
    }

    if (jobs.length > 1) {
      this.planningAlertsService.bulkDropJobsSuccess(jobs.length);
    }

    return {
      jobIds: jobs.map(job => job.id),
      fromRouteId: fromRouteId,
      toRouteId: route.id,
      fromOrderedIds: fromOrderedIds,
      toOrderedIds: _.concat(this._getStopsOrderIds(route.activities), stopIds),
      allowSkillsMismatch: allowSkillsMismatch
    };
  }

  // --------------------------------------------------------------------------------
  // 3. planning-draft-resources-panel-list.component.ts & planning-draft-resources-table-list.component.ts
  // --------------------------------------------------------------------------------
  public onDropResources(
    resource: any,
    event: CdkDragDrop<string[]>,
    selectedJobIds: string[],
    jobs: any[]
  ): Promise<{ type: 'update' | 'create', data: any } | null> {
    if (!event.isPointerOverContainer) {
      return Promise.resolve(null);
    }

    const isRoute: boolean = !!_.get(event.item.data, 'activities');

    if (isRoute) {
      return Promise.resolve({
        type: 'update',
        data: { route: event.item.data, resourceId: resource.id }
      });
    }

    const isJob = !!_.get(event.item.data, 'jobType');
    const isActivity = !!_.get(event.item.data, 'stopType');
    const jobId = isJob ? _.get(event.item.data, 'id') : _.get(event.item.data, 'jobId');
    const eventIds = event.item.data.jobIds || [jobId];
    const jobIds = _.intersectionWith(eventIds, selectedJobIds, _.isEqual)?.length ?
      selectedJobIds :
      eventIds;

    if ((!jobId && !jobIds) || (!isJob && !isActivity)) {
      return Promise.resolve(null);
    }

    if (jobIds.length >= 10) {
      return this.confirmDialogService.confirmWithInfo('Confirm', 'MOVE_X_JOBS_CONFIRM', '', {x: jobIds.length}, event)
        .then(() => {
          return this._displayJobsWarningsResources(resource, jobIds, event, jobs);
        })
        .catch(() => null);
    } else {
      return this._displayJobsWarningsResources(resource, jobIds, event, jobs);
    }
  }

  private _displayJobsWarningsResources(
    resource: any,
    jobIds: string[],
    event: CdkDragDrop<string[]>,
    jobs: any[]
  ): Promise<{ type: 'update' | 'create', data: any } | null> {
    const errorCounts: Record<string, number> = {};
    const validJobsToMove = [];
    const jobsToMove = [];
    const stopIds = [];

    jobs.forEach((job) => {
      if (jobIds.includes(job.id)) {
        jobsToMove.push(job);
        validJobsToMove.push(job);
        stopIds.push(..._.map(_.get(job, 'stops') || [], 'id'));
      }
    });

    const warnings: { message: string, type?: string, data?: any }[] = this.planningDialogService.getJobsSkillMismatchWarnings(jobsToMove, resource);
    warnings.push(...this.planningDialogService.getJobsCapacityWarnings(jobsToMove, resource, true));
    const hasSkillMismatch = warnings.some((w) => w.type === 'skillMismatch');

    if (warnings.length) {
      return this.planningDialogService.openJobAssignWarnings(warnings, event)
        .then(() => {
          return this._dropJobsResources(resource, validJobsToMove, errorCounts, hasSkillMismatch);
        })
        .catch(() => null);
    } else {
      return Promise.resolve(this._dropJobsResources(resource, validJobsToMove, errorCounts, hasSkillMismatch));
    }
  }

  private _dropJobsResources(resource: any, jobs: any[], errorCounts: Record<string, number>, allowSkillsMismatch?: boolean): { type: 'create', data: any } | null {
    const errorKeys = Object.keys(errorCounts);

    if (errorKeys.length >= 1) {
      this.planningAlertsService.bulkDropJobsError(errorCounts);
    }

    if (!jobs.length) {
      return null;
    }

    if (jobs.length > 1) {
      this.planningAlertsService.bulkDropJobsSuccess(jobs.length);
    }

    return {
      type: 'create',
      data: { id: resource.id, jobIds: jobs.map(job => job.id), allowSkillsMismatch: allowSkillsMismatch }
    };
  }

  // --------------------------------------------------------------------------------
  // 4. planning-draft-resource-panel-view.component.ts & planning-draft-resource-table-drilldown.component.ts
  // --------------------------------------------------------------------------------
  public onDropResourceRoutes(
    event: CdkDragDrop<any>,
    resource: any,
    selectedJobIds: string[],
    setHovered: (hovered: boolean) => void,
    jobs: any[]
  ): Promise<{ type: 'sequence' | 'update' | 'create', data: any } | null> {
    if (!event.isPointerOverContainer) {
      return Promise.resolve(null);
    }

    // Routes - Move Or Resequence
    if (event.item.data.activities) {
      if (event.previousContainer === event.container) {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        const orderedRouteIds: string[] = event.container.data.map((r: any) => {
          return r.id;
        });
        const routeGroupId = event.item.data.routeGroupId;
        setHovered(false);
        return Promise.resolve({
          type: 'sequence',
          data: { orderedRouteIds: orderedRouteIds, routeGroupId: routeGroupId }
        });
      } else {
        transferArrayItem(
          event.previousContainer.data,
          event.container.data,
          event.previousIndex,
          event.currentIndex
        );
        setHovered(false);
        return Promise.resolve({
          type: 'update',
          data: { route: event.item.data, resourceId: resource.id }
        });
      }
    }

    // Jobs - Create Route
    if (event.item.data.jobId || event.item.data.jobType || event.item.data.jobIds) {
      return this._onDropJobsResourceRoutes(event, resource, selectedJobIds, setHovered, jobs);
    }

    return Promise.resolve(null);
  }

  private _onDropJobsResourceRoutes(
    event: CdkDragDrop<any[]>,
    resource: any,
    selectedJobIds: string[],
    setHovered: (hovered: boolean) => void,
    jobs: any[]
  ): Promise<{ type: 'create', data: any } | null> {
    const isJob = !!_.get(event.item.data, 'jobType');
    const isActivity = !!_.get(event.item.data, 'stopType') || !!_.get(event.item.data, 'type');
    const jobId = isJob ? _.get(event.item.data, 'id') : _.get(event.item.data, 'jobId');
    const eventIds = event.item.data.jobIds || [jobId];
    const jobIds = _.intersectionWith(eventIds, selectedJobIds, _.isEqual)?.length ?
      selectedJobIds :
      eventIds;

    let routeJobIds: string[] = [];

    if (jobIds?.length) {
      routeJobIds = jobIds;
    } else if (jobId) {
      routeJobIds = [jobId];
    }

    if (routeJobIds.length === 0) {
      setHovered(false);
      return Promise.resolve(null);
    }

    const warnings = this.planningDialogService.getJobsSkillMismatchWarnings(
      _.filter(jobs, (job) => routeJobIds.includes(job.id)),
      resource
    );
    const data: any = { id: resource.id, jobIds: routeJobIds, optimize: !isActivity, allowSkillsMismatch: warnings.length > 0 };

    if (isActivity) {
      data.stopIdOrder = this._getStopsOrderIds(
        _.filter(event.previousContainer.data, (a: any) => _.intersection(a.jobIds || [a.jobId], routeJobIds).length > 0)
      );
    }

    setHovered(false);

    if (warnings.length) {
      return this.planningDialogService.openJobAssignWarnings(warnings, event)
        .then(() => ({ type: 'create' as const, data }))
        .catch(() => null);
    }

    return Promise.resolve({ type: 'create', data });
  }

  // --------------------------------------------------------------------------------
  // 5. planning-draft-route-panel-view-test.component.ts & planning-draft-route-table-drilldown.component.ts
  // --------------------------------------------------------------------------------
  public onDropRouteActivities(
    event: CdkDragDrop<string[]>,
    selectedJobIds: string[],
    jobsMap: Record<string, any>,
    route: any,
    isValidateRouteVehicles: boolean,
    isAllowDraggingIntoDispatchedRoute: boolean,
    jobs: any[]
  ): Promise<any | null> {
    if (!event.isPointerOverContainer) {
      return Promise.resolve(null);
    }

    // Add Job To Route
    if (event.item.data.jobType || event.item.data.groupedByType) {
      const jobId = _.get(event.item.data, 'id');
      const ids = event.item.data.jobIds || [jobId];
      const jobIds = _.intersectionWith(ids, selectedJobIds, _.isEqual)?.length ? selectedJobIds : ids;

      const jobVehicleTypes = _.map(jobsMap[jobId]?.allowedVehicleReferences, 'type');

      for (let id of jobIds) {
        jobVehicleTypes.push(..._.map(jobsMap[jobId]?.allowedVehicleReferences, 'type'));
      }

      if (jobIds.length >= 10) {
        return this.confirmDialogService.confirmWithInfo('Confirm', 'MOVE_X_JOBS_CONFIRM', '', {x: jobIds.length}, event)
          .then(() => {
            return this._displayJobsWarningsRouteActivities(jobIds, jobVehicleTypes, event, jobs, route, isValidateRouteVehicles, isAllowDraggingIntoDispatchedRoute);
          })
          .catch(() => null);
      }

      return this._displayJobsWarningsRouteActivities(jobIds, jobVehicleTypes, event, jobs, route, isValidateRouteVehicles, isAllowDraggingIntoDispatchedRoute);
    }
    // Add Activity To Route
    else {
      const jobId = _.get(event.item.data, 'jobId');
      const ids = event.item.data.jobIds || [jobId];
      const jobIds = _.intersectionWith(ids, selectedJobIds, _.isEqual)?.length ? selectedJobIds : ids;

      const itemJobs = _.map(jobIds, (id) => {
        return jobsMap[id];
      });

      const fromRouteId = _.get(event.previousContainer, 'data[0].routeId');
      const toRouteId = route.id;

      if (fromRouteId !== toRouteId) {
        if (jobIds.length >= 10) {
          return this.confirmDialogService.confirmWithInfo('Confirm', 'MOVE_X_JOBS_CONFIRM', '', {x: jobIds.length}, event)
            .then(() => {
              return this._displayActivitiesWarningRouteActivities(jobIds, itemJobs, fromRouteId, toRouteId, event, isValidateRouteVehicles, route);
            })
            .catch(() => null);
        }

        return this._displayActivitiesWarningRouteActivities(jobIds, itemJobs, fromRouteId, toRouteId, event, isValidateRouteVehicles, route);
      }

      return Promise.resolve(this._activityDropRouteActivities(jobIds, itemJobs, fromRouteId, toRouteId, event, route));
    }
  }

  private _displayActivitiesWarningRouteActivities(
    jobIds: string[],
    itemJobs: any[],
    fromRouteId: string,
    toRouteId: string,
    event: any,
    isValidateRouteVehicles: boolean,
    route: any
  ): Promise<any | null> {
    let isValidateRouteVehicle = false;

    for (let job of itemJobs) {
      const jobVehicleTypes = _.map(job.allowedVehicleReferences, 'type');
      if (isValidateRouteVehicles && !!route.resource?.vehicleClass &&
        jobVehicleTypes?.length && !jobVehicleTypes.includes(route.resource.vehicleClass)) {
        isValidateRouteVehicle = true;
        break;
      }
    }

    const warnings: { message: string, type?: string, data?: any }[] = this.planningDialogService.getJobsSkillMismatchWarnings(itemJobs, route.resource);

    if (isValidateRouteVehicle) {
      warnings.push({
        message: itemJobs.length > 1 ? "Services vehicle types do not match route resource vehicle type. Are you sure you want to proceed?" :
          "Service vehicle types do not match route resource vehicle type. Are you sure you want to proceed?"
      });
    }

    warnings.push(...this.planningDialogService.getJobsCapacityWarnings(itemJobs, route, true));
    const hasSkillMismatch = warnings.some((w) => w.type === 'skillMismatch');

    if (warnings.length) {
      return this.planningDialogService.openJobAssignWarnings(warnings, event)
        .then(() => {
          return this._activityDropRouteActivities(jobIds, itemJobs, fromRouteId, toRouteId, event, route, hasSkillMismatch);
        })
        .catch(() => null);
    }

    return Promise.resolve(this._activityDropRouteActivities(jobIds, itemJobs, fromRouteId, toRouteId, event, route, hasSkillMismatch));
  }

  private _activityDropRouteActivities(
    jobIds: string[],
    itemJobs: any[],
    fromRouteId: string,
    toRouteId: string,
    event: any,
    route: any,
    allowSkillsMismatch?: boolean
  ) {
    const isLockedJobMove = fromRouteId !== toRouteId && _.some(itemJobs, (job) => !!job?.lockedRouteId);
    const sourceActivities = isLockedJobMove ? [...event.previousContainer.data] : event.previousContainer.data;
    const targetActivities = isLockedJobMove ? [...event.container.data] : event.container.data;

    this._moveSelectedActivities(
      sourceActivities,
      targetActivities,
      jobIds,
      event.previousIndex,
      event.currentIndex
    );

    if ((event.previousContainer === event.container) && (event.currentIndex === event.previousIndex)) {
      return null;
    }

    let pickupIndex: number | undefined;
    let destinationIndex: number | undefined;

    if (fromRouteId !== toRouteId) {
      const stopType = event.item.data.stopType;
      if (stopType === 'Pickup') {
        pickupIndex = Math.max(0, event.currentIndex - 1);
      } else if (stopType === 'Dropoff') {
        destinationIndex = Math.max(0, event.currentIndex - 1);
      }
    }

    return {
      jobIds: jobIds,
      fromRouteId: fromRouteId,
      toRouteId: toRouteId,
      fromOrderedIds: this._getStopsOrderIds(sourceActivities),
      toOrderedIds: (fromRouteId !== toRouteId) ? this._getStopsOrderIds(targetActivities) : undefined,
      pickupIndex: pickupIndex,
      destinationIndex: destinationIndex,
      allowSkillsMismatch: allowSkillsMismatch
    };
  }

  private _displayJobsWarningsRouteActivities(
    jobIds: string[],
    jobVehicleTypes: any[],
    event: CdkDragDrop<string[]>,
    jobs: any[],
    route: any,
    isValidateRouteVehicles: boolean,
    isAllowDraggingIntoDispatchedRoute: boolean
  ): Promise<any | null> {
    const errorCounts: Record<string, number> = {};
    const validJobsToMove = [];
    const jobsToMove = [];
    const stopIds = [];

    jobs.forEach((job) => {
      if (jobIds.includes(job.id)) {
        const error = this.planningAlertsService.getJobValidationError(job,
          route,
          { isAllowDraggingIntoDispatchedRoute: isAllowDraggingIntoDispatchedRoute }
        );

        jobsToMove.push(job);

        if (!error) {
          validJobsToMove.push(job);
          stopIds.push(..._.map(_.get(job, 'stops') || [], 'id'));
        } else {
          errorCounts[error] = (errorCounts[error] || 0) + 1;
        }
      }
    });

    const warnings: { message: string, type?: string, data?: any }[] = this.planningDialogService.getJobsSkillMismatchWarnings(jobsToMove, route.resource);

    if (isValidateRouteVehicles && !!route.resource?.vehicleClass &&
      jobVehicleTypes?.length && !jobVehicleTypes.includes(route.resource.vehicleClass)) {
      warnings.push({
        message: validJobsToMove.length > 1 ? "Services vehicle types do not match route resource vehicle type. Are you sure you want to proceed?" :
          "Service vehicle types do not match route resource vehicle type. Are you sure you want to proceed?"
      });
    }

    warnings.push(...this.planningDialogService.getJobsCapacityWarnings(jobsToMove, route, true));
    const hasSkillMismatch = warnings.some((w) => w.type === 'skillMismatch');

    if (warnings.length) {
      return this.planningDialogService.openJobAssignWarnings(warnings, event)
        .then(() => {
          return this._dropJobsRouteActivities(validJobsToMove, errorCounts, stopIds, route, event, hasSkillMismatch);
        })
        .catch(() => null);
    } else {
      return Promise.resolve(this._dropJobsRouteActivities(validJobsToMove, errorCounts, stopIds, route, event, hasSkillMismatch));
    }
  }

  private _dropJobsRouteActivities(jobs: any[], errorCounts: Record<string, number>, stopIds: string[], route: any, event: CdkDragDrop<string[]>, allowSkillsMismatch?: boolean) {
    const errorKeys = Object.keys(errorCounts);

    if (errorKeys.length) {
      this.planningAlertsService.bulkDropJobsError(errorCounts);
    }

    if (!jobs.length) {
      return null;
    }

    if (jobs.length > 1) {
      this.planningAlertsService.bulkDropJobsSuccess(jobs.length);
    }

    const offset: number = route.activities.some(a => a.type === 'START') ? 1 : 0;

    return {
      jobIds: jobs.map(job => job.id),
      toRouteId: route.id,
      destinationIndex: Math.max(0, event.currentIndex - offset),
      allowSkillsMismatch: allowSkillsMismatch
    };
  }

  // --------------------------------------------------------------------------------
  // 6. planning-draft-jobs-table-list.component.ts & planning-draft-jobs-panel-editable-list.component.ts
  // --------------------------------------------------------------------------------
  public onDropJobs(
    event: CdkDragDrop<string[]>,
    selectedIds: string[]
  ): Promise<string[] | null> {
    if (!event.isPointerOverContainer || event.container === event.previousContainer) {
      return Promise.resolve(null);
    }

    let jobIds = event.item.data.jobIds || [event.item.data.jobId];

    if (selectedIds?.length && _.includes(selectedIds, event.item.data.jobId)) {
      jobIds = selectedIds;
    }

    return Promise.resolve(jobIds);
  }

  // --------------------------------------------------------------------------------
  // Shared by planning-draft-route-table-drilldown.component.ts & planning-draft-route-panel-view-test.component.ts
  // (both delegate their cdkDropListSortPredicate here)
  // --------------------------------------------------------------------------------
  public canReorderRouteActivity(
    index: number,
    item: CdkDrag<any>,
    drop: CdkDropList,
    selectedJobIds: string[]
  ): boolean {
    const stopType = item.data.stopType;
    const itemJobIds = item.data.jobIds || [item.data.jobId];
    const isMultiSelectDrag = selectedJobIds?.length > 1 && !!_.intersection(selectedJobIds, itemJobIds).length;
    const moveJobIds = isMultiSelectDrag ? selectedJobIds : itemJobIds;

    const isFullyCovered = (a: any) => {
      return _.difference(a.jobIds || [a.jobId], moveJobIds).length === 0;
    };

    const group = isMultiSelectDrag ?
      _.filter(drop.data, (a: any) => a.id === item.data.id || isFullyCovered(a)) :
      [item.data];

    if (group.length <= 1) {
      const connectedIndexes = [];
      drop.data.forEach((a: any, i: number) => {
        const jobIds = a.jobIds || [a.jobId];
        if ((item.data.id !== a.id) && !!_.intersection(jobIds, itemJobIds).length) {
          connectedIndexes.push(i);
        }
      });

      if (!connectedIndexes.length) {
        return true;
      }

      let canMove = true;
      if (stopType === 'Pickup') {
        connectedIndexes.forEach((i) => {
          if (i <= index) {
            canMove = false;
          }
        });
      } else if (stopType === 'Dropoff') {
        connectedIndexes.forEach((i) => {
          if (i >= index) {
            canMove = false;
          }
        });
      }
      return canMove;
    }

    const groupIds = new Set(_.map(group, 'id'));
    const draggedGroupIndex = _.findIndex(group, (a: any) => a.id === item.data.id);
    const blockStart = _.clamp(index - draggedGroupIndex, 0, drop.data.length - group.length);
    const blockEnd = blockStart + group.length - 1;

    let canMove = true;

    // Only external (non-group) links need checking here: `group` is filtered out of `drop.data`
    // in its original order, and any two group members that are themselves a linked Pickup/Dropoff
    // pair must already satisfy pickup-before-dropoff in that original order (a pre-existing
    // invariant of the route), so their relative order survives the block move unchanged.
    group.forEach((groupMember: any) => {
      const memberJobIds = groupMember.jobIds || [groupMember.jobId];
      const memberConnectedIndexes = [];

      drop.data.forEach((a: any, i: number) => {
        if (groupIds.has(a.id)) {
          return;
        }
        if (_.intersection(a.jobIds || [a.jobId], memberJobIds).length) {
          memberConnectedIndexes.push(i);
        }
      });

      if (groupMember.stopType === 'Pickup') {
        memberConnectedIndexes.forEach((i) => {
          if (i <= blockEnd) {
            canMove = false;
          }
        });
      } else if (groupMember.stopType === 'Dropoff') {
        memberConnectedIndexes.forEach((i) => {
          if (i >= blockStart) {
            canMove = false;
          }
        });
      }
    });

    return canMove;
  }

  private _moveSelectedActivities(
    sourceActivities: any[],
    targetActivities: any[],
    jobIds: string[],
    previousIndex: number,
    currentIndex: number
  ): void {
    const isSameContainer = sourceActivities === targetActivities;

    if (isSameContainer && previousIndex === currentIndex) {
      return;
    }

    const draggedActivity = sourceActivities[previousIndex];
    const draggedOwnIds = draggedActivity.jobIds || [draggedActivity.jobId];
    const isMultiSelectDrag = _.difference(jobIds, draggedOwnIds).length > 0;

    const moveSingleItem = () => {
      if (isSameContainer) {
        moveItemInArray(sourceActivities, previousIndex, currentIndex);
      } else {
        transferArrayItem(sourceActivities, targetActivities, previousIndex, currentIndex);
      }
    };

    if (!isMultiSelectDrag) {
      moveSingleItem();
      return;
    }

    const isFullyCovered = (activity: any) => {
      return _.difference(activity.jobIds || [activity.jobId], jobIds).length === 0;
    };

    const group = _.filter(sourceActivities, (a: any) => a.id === draggedActivity.id || isFullyCovered(a));

    if (group.length <= 1) {
      moveSingleItem();
      return;
    }

    const groupIds = new Set(_.map(group, 'id'));
    const draggedGroupIndex = _.findIndex(group, (a: any) => a.id === draggedActivity.id);
    const remaining = _.filter(sourceActivities, (a: any) => !groupIds.has(a.id));

    sourceActivities.splice(0, sourceActivities.length, ...remaining);

    const insertAt = _.clamp(currentIndex - draggedGroupIndex, 0, targetActivities.length);
    targetActivities.splice(insertAt, 0, ...group);
  }

  private _getStopsOrderIds(activities: any[]) {
    return _.chain(activities)
      .filter((a: any) => {
        return _.isEqual(a.type, 'STOP') || _.isEqual(a.type, 'GROUP');
      })
      .map((filteredItem: any) => {
        return filteredItem.stopId || filteredItem.stopIds;
      })
      .flatten()
      .value();
  }

}
